import { useCallback, useEffect, useRef, useState } from 'react';
import type { InviteCampaign, UpdateInviteCampaignInput } from '@peles/shared';
import { useUpdateInviteCampaign } from '../../../hooks/useInvites';
import { useDialog } from '../../../context/DialogContext';

/**
 * A step's own copy of what it edits, saved when asked. Started from the
 * campaign as loaded, and started again when a different campaign is opened
 * or the step is saved — never while somebody is typing, so a refetch in the
 * background cannot wipe an edit.
 */
export function useStepDraft<T extends object>(
  campaign: InviteCampaign,
  fromCampaign: (campaign: InviteCampaign) => T,
  toUpdate: (draft: T, campaign: InviteCampaign) => UpdateInviteCampaignInput,
  onDirtyChange?: (dirty: boolean) => void,
) {
  const [draft, setDraft] = useState<T>(() => fromCampaign(campaign));
  const [dirty, setDirty] = useState(false);
  const updateCampaign = useUpdateInviteCampaign();
  const { alert } = useDialog();
  const loadedFor = useRef(campaign.id);

  useEffect(() => {
    if (loadedFor.current === campaign.id) return;
    loadedFor.current = campaign.id;
    setDraft(fromCampaign(campaign));
    setDirty(false);
    // Only a different campaign starts the draft again.
  }, [campaign.id]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const set = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setDirty(true);
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    try {
      const saved = await updateCampaign.mutateAsync({
        id: campaign.id,
        updates: toUpdate(draft, campaign),
      });
      setDraft(fromCampaign(saved));
      setDirty(false);
      return true;
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Could not save the campaign',
        variant: 'error',
      });
      return false;
    }
    // `toUpdate`/`fromCampaign` are the step's own, fixed functions.
  }, [alert, campaign, draft, updateCampaign]);

  return { draft, set, dirty, save, saving: updateCampaign.isPending };
}

/** The campaign's settings with a step's changes laid over them. */
export function withSettings(
  campaign: InviteCampaign,
  changes: InviteCampaign['settings'],
): InviteCampaign['settings'] {
  return { ...(campaign.settings ?? {}), ...changes };
}

/** A date field's value for a stored deadline (the day it falls on, UTC). */
export function toDateInputValue(value: Date | string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

/** A deadline at the end of the day picked. */
export function fromDateInputValue(value: string): string | null {
  return value ? new Date(`${value}T23:59:59.000Z`).toISOString() : null;
}
